import * as _ from 'lodash';
import { AbstractControl, UntypedFormControl, FormGroupDirective, NgForm, FormControl, } from '@angular/forms';
import { booleanAttribute, Component, computed, effect, EventEmitter, HostBinding, HostListener, inject, input, InputSignal, OnDestroy, OnInit, Output, signal, Signal } from '@angular/core';
import { ErrorStateMatcher } from '@angular/material/core';
import { Subscription } from 'rxjs';

import { TranslocoService } from '@jsverse/transloco';

import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';

export class InputErrorStateMatcher extends ErrorStateMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const isSubmitted = form && form.submitted;
        return !!(
            control &&
            control.invalid &&
            (control.dirty || control.touched || isSubmitted)
        );
    }
}

@Component({
    selector: 'app-base-control',
    template: '',
    styleUrls: [],
})
export abstract class _BaseControlComponent
{
    private __loadSubscr?: Subscription;

    protected readonly _loadingPageService = injectRequired(LoadingPageService);
    protected readonly _transloco = injectRequired(TranslocoService);

    abstract label: string;

    abstract ngOnInit(): void;
    abstract ngOnDestroy(): void;

    abstract attributes$$: Signal<Record<string, string>>;
    abstract autofocus$$: InputSignal<boolean>;
    abstract control$$: Signal<FormControl>;
    abstract controlName$$: InputSignal<string>;
    abstract disabled$$: Signal<boolean>;
    abstract errorShow$$: InputSignal<boolean>;
    abstract errorStateMatcher$$: InputSignal<ErrorStateMatcher>;
    abstract id$$: InputSignal<string>;
    abstract lockOnLoad$$: InputSignal<boolean>;
    abstract placeholder$$: InputSignal<string>;
    abstract readonly$$: InputSignal<boolean>;
    abstract subscriptHide$$: InputSignal<boolean>;
    abstract tabIndex$$: Signal<string>;
    abstract value$$: InputSignal<any>;
    abstract valueReset$$: InputSignal<any>;

    protected _input_attributes$$ = input<Record<string, string>>({}, {alias: 'attributes'});
    protected _input_autofocus$$ = input<boolean>(false, {alias: 'autofocus'});
    protected _input_control$$ = input<FormControl>(new FormControl(), {alias: 'control'});
    protected _input_controlName$$ = input<string>('', {alias: 'controlName'});
    protected _input_disabled$$ = input(false, { transform: booleanAttribute, alias: 'disabled' });
    protected _input_errorShow$$ = input<boolean>(true, { alias: 'showError' });
    // inject() i.p.v. `new ErrorStateMatcher()`: anders omzeilt dit de app-brede DI-override in
    // main.ts (ErrorStateMatcher -> ShowOnDirtyErrorStateMatcher) en krijg je altijd de kale
    // Material-default terug, die op control.touched (blur) checkt i.p.v. control.dirty (typen).
    protected _input_errorStateMatcher$$ = input<ErrorStateMatcher>(inject(ErrorStateMatcher), {alias: 'errorStateMatcher'});
    protected _input_id$$ = input<string>('', {alias: 'id'});
    protected _input_lockOnLoad$$ = input<boolean>(false, {alias: 'lockOnLoad'});
    protected _input_placeholder$$ = input<string>('', {alias: 'placeholder'});
    protected _input_readonly$$ = input<boolean>(false, {alias: 'readonly'});
    protected _input_subscriptHide$$ = input<boolean>(false, {alias: 'subscriptHide'});
    protected _input_tabIndex$$ = input<string>('0', {alias: 'tabIndex'});
    protected _input_value$$ = input<any>(null, {alias: 'value'});
    protected _input_valueReset$$ = input<any>(undefined, { alias: 'resetValue' });

    protected _control$$ = signal<FormControl>(new FormControl(''));
    // Afgeleid i.p.v. eenmalig in ngOnInit gezet: readonly kan na het tonen nog wisselen (bv. een
    // recht dat pas met een latere response binnenkomt), en dan moet het veld ook weer tabbaar zijn.
    protected _tabIndex$$: Signal<string> = computed(() => this._input_readonly$$() ? '-1' : this._input_tabIndex$$());

    @Output('onEscape') escEmit = new EventEmitter();
    @Output('onReset') resetEmit = new EventEmitter();

    // Getter i.p.v. eenmalige waarde: readonly kan na het tonen nog wisselen (zie _tabIndex$$).
    @HostBinding('class.readonly') get classReadOnly(): boolean { return this._input_readonly$$(); }
    @HostBinding('class.input-control') classInputControl = true;

    // @HostListener('document:keydown', ['$event']) handleKeyboardEvent(event: KeyboardEvent) {
    //     if (event.key === 'Escape') {
    //         this.emit_esc(event);
    //     }
    // }

    protected _ngOnInit(): void {
        this._control$$.set(this._input_control$$());
        this.__loadSubscr = this._loadingPageService.isLoading$.subscribe(loading => {
            if (this._input_lockOnLoad$$()) {this.__control_disable(loading);}
        });
    }

    emit_esc(event: Event): void {
        if (this._input_control$$()?.dirty) {
            event.stopPropagation();
            this.control_reset();
        } else {
            this.__esc_emit();
        }
    }

    private __control_disable(loading: boolean) {
        if (loading) {
            if (this._input_control$$()?.enabled) {
                this._input_control$$()?.disable();
            }
        } else {
            if (this._input_control$$()?.disabled) {
                this._input_control$$()?.enable();
            }
        }
    }

    control_reset(): void {
        setTimeout(() => {
            this._input_control$$()?.reset(this._input_valueReset$$());
            this.__reset_emit();
        }, 0);
    }

    private __esc_emit() {
        this.escEmit.emit();
    }

    errorMessage_getFirst(): string {
        return this._validationMessage(this._input_control$$()?.errors);
    }

    // Vertaalt de eerste validator-fout naar tekst via de validation.<key>-namespace.
    protected _validationMessage(errors: Record<string, any> | null | undefined): string {
        const _keys = Object.keys(errors ?? {});
        if (_keys.length === 0) return '';

        const _key = _keys[0];
        const _value = (errors as Record<string, any>)[_key];
        const _params: Record<string, unknown> = { field: this.label };
        if (_value && typeof _value === 'object') Object.assign(_params, _value);

        return this._transloco.translate(`validation.${_key}`, _params);
    }

    private __reset_emit() {
        this.resetEmit.emit();
    }

    protected _ngOnDestroy(): void {
        this.__loadSubscr?.unsubscribe();
        this.escEmit?.unsubscribe();
        this.resetEmit?.unsubscribe();
   }
}
